import { useEffect, useState } from 'react';
import API from '../api/api';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from '../contexts/SnackbarContext';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import InventoryIcon from '@mui/icons-material/Inventory2';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EuroIcon from '@mui/icons-material/Euro';
import SavingsIcon from '@mui/icons-material/Savings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateDisplay = (isoDate) => {
  if (!isoDate) return '-';
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export default function Dashboard() {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayIsoDate());

  const fetchDashboard = async (date) => {
    try {
      setLoading(true);
      const res = await API.get('/dashboard', {
        params: date ? { date } : undefined,
      });
      setData(res.data);
      setError('');
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors du chargement du tableau de bord';
      setError(message);
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(selectedDate);
  }, [selectedDate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--app-bg)',
        }}
      >
        <CircularProgress sx={{ color: 'var(--text-primary)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          p: { xs: 3, md: 6 },
          background: 'var(--app-bg)',
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 1.5, sm: 2.5, md: 6 },
        py: { xs: 3, sm: 4, md: 5 },
        background: 'var(--app-bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* BACKGROUND GLOWS */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: { xs: 240, md: 360 },
          height: { xs: 240, md: 360 },
          background: 'var(--glow-a)',
          filter: 'blur(6px)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -160,
          left: -120,
          width: { xs: 260, md: 420 },
          height: { xs: 260, md: 420 },
          background: 'var(--glow-b)',
          filter: 'blur(10px)',
          zIndex: 0,
        }}
      />

      {/* HERO + FILTER */}
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        alignItems="center"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: { xs: 2, sm: 2.5, md: 3.5 },
              borderRadius: 4,
              background:
                'var(--surface-2)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Stack spacing={1}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: 'var(--text-primary)',
                  letterSpacing: 1,
                  fontSize: { xs: 24, sm: 32, md: 44 },
                }}
              >
                Tableau de bord
              </Typography>
              <Typography sx={{ color: 'var(--text-secondary)' }}>
                Vue globale de l'activite, du stock et des ventes.
              </Typography>
              <Typography sx={{ color: 'var(--text-muted)' }}>
                {formatDateDisplay(selectedDate)}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 4,
              background:
                'var(--surface-1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <Stack spacing={2}>
              <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                Filtre par jour
              </Typography>
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="small"
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
                inputProps={{ 'aria-label': 'Datum' }}
              />
              <Button
                variant="contained"
                onClick={() => setSelectedDate(getTodayIsoDate())}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Aujourd'hui
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* KPI GRID */}
      <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 2, md: 3 } }}>
        {[
          { label: 'Total pneus', value: data?.totalTires, icon: <TireRepairIcon />, accent: '#6366f1' },
          { label: 'Stock total', value: data?.totalStock, icon: <InventoryIcon />, accent: '#22c55e' },
          { label: 'Partenaires', value: data?.totalPartners, icon: <GroupIcon />, accent: '#38bdf8' },
          { label: 'Pneus vendus', value: data?.totalSoldTires, icon: <ShoppingCartIcon />, accent: '#f59e0b' },
          { label: 'Chiffre d\'affaires', value: formatCurrency(data?.totalRevenue), icon: <EuroIcon />, accent: '#a78bfa' },
          { label: 'Kasse', value: formatCurrency(data?.cashBalance), icon: <SavingsIcon />, accent: '#10b981' },
          { label: 'Depenses ouvertes', value: data?.openExpenses, icon: <ReceiptLongIcon />, accent: '#ef4444' },
        ].map((item, index, arr) => (
          <Grid
            item
            xs={isMobile && index === arr.length - 1 ? 12 : 6}
            sm={6}
            md={4}
            key={item.label}
          >
            <Paper
              sx={{
                p: { xs: 1.6, sm: 2, md: 2.5 },
                borderRadius: { xs: 3, sm: 4 },
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${item.accent}55, transparent 70%)`,
                }}
              />
              <Stack
                direction={isMobile ? 'row' : 'row'}
                spacing={1.2}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 40, md: 42 },
                    height: { xs: 36, sm: 40, md: 42 },
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${item.accent}22`,
                    color: item.accent,
                    '& svg': {
                      fontSize: { xs: 18, sm: 20, md: 22 },
                    },
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: 'var(--text-muted)', letterSpacing: 0.6 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: 'var(--text-primary)',
                      fontSize: { xs: 18, sm: 20, md: 24 },
                    }}
                  >
                    {item.value ?? '-'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 3, md: 3 }} sx={{ mt: { xs: 1, md: 2 } }}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              background:
                'var(--surface-1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              Stock par reference
            </Typography>
            <Divider sx={{ my: 2, opacity: 0.2 }} />
            {isMobile ? (
              <Stack spacing={2}>
                {(data?.stockByReference || []).map((row) => (
                  <Paper
                    key={row.tire_id}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: 'var(--surface-3)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {row.reference}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 2,
                          background: row.quantity > 0 ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
                          color: row.quantity > 0 ? '#22c55e' : '#ef4444',
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {row.quantity}
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <TableContainer
                sx={{
                  borderRadius: 3,
                  overflowX: 'auto',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 360,
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid var(--border-subtle)',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: 'var(--text-primary)',
                          fontWeight: 800,
                          backgroundColor: 'var(--table-header-bg)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Reference
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'var(--text-primary)',
                          fontWeight: 800,
                          backgroundColor: 'var(--table-header-bg)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Quantite
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.stockByReference || []).map((row) => (
                      <TableRow
                        key={row.tire_id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.reference}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              background:
                'var(--surface-1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                Derniers logs
              </Typography>
            </Stack>
            <Divider sx={{ my: 2, opacity: 0.2 }} />
            {isMobile ? (
              <Stack spacing={2}>
                {(data?.lastLogs || []).map((row) => (
                  <Paper
                    key={row.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: 'var(--surface-3)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Stack spacing={0.6}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {row.reference}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 2,
                            background: 'rgba(59,130,246,0.2)',
                            color: '#60a5fa',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {row.action}
                        </Box>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          Quantite: {row.quantity}
                        </Typography>
                      </Stack>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        Utilisateur: {row.username}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <TableContainer
                sx={{
                  borderRadius: 3,
                  overflowX: 'auto',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 520,
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid var(--border-subtle)',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {['Reference', 'Action', 'Quantite', 'Utilisateur'].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 800,
                            backgroundColor: 'var(--table-header-bg)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.lastLogs || []).map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.reference}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.action}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.quantity}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.username}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {data?.sellerStats && data.sellerStats.length > 0 && (
        <Grid container spacing={{ xs: 3, md: 3 }} sx={{ mt: { xs: 1, md: 2 } }}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                Ventes par employe (mois)
              </Typography>
              <Divider sx={{ my: 2, opacity: 0.2 }} />
              {isMobile ? (
                <Stack spacing={2}>
                  {data.sellerStats.map((row) => (
                    <Paper
                      key={row.user_id}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        background: 'var(--surface-3)',
                        border: '1px solid var(--border-subtle)',
                      }}
                    >
                      <Stack spacing={0.6}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                          {row.username}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          Umsatz: {formatCurrency(row.revenue)}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          Menge: {row.quantity}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Employe', 'Umsatz', 'Menge'].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              color: 'var(--text-primary)',
                              fontWeight: 700,
                              backgroundColor: 'var(--table-header-bg)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.sellerStats.map((row) => (
                        <TableRow key={row.user_id}>
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.username}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {formatCurrency(row.revenue)}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.quantity}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}




